// Persistent save data. Meta-progression: player level, XP, skill points,
// unlocked towers, codex. All meta features read/write through `save` (the
// singleton at the bottom).
//
// Storage is abstracted behind `SaveStore` so the same SaveSystem can run
// against localStorage today and a cloud backend (Supabase) later. Swap via
// `setSaveStore()` before `save.load()` is called.
//
// Data is versioned. `clientRev` is a monotonic counter bumped on every
// write — used by future cloud sync for optimistic concurrency.

const STORAGE_KEY = "mittelalte.save.v1";
const SAVE_VERSION = 1;

// The Vite dev server exposes the full campaign for fast map/difficulty
// testing. Keep this list local to the save boundary so every consumer
// (atlas, profile, and difficulty gate) sees the same three-star state.
const DEV_CAMPAIGN_MAP_KEYS = [
  "greenwold_pass", "stonebridge", "frostfell_gorge",
  "whispering_woods", "dragons_spine", "sunken_marsh",
  "goldmere_coast", "ironforge_app", "twilight_chasm",
] as const;

import { AUDIO_DEFAULTS } from "../config";

/** Player Level cap. Past 120 levels are gained but no further skill points. */
export const MAX_PLAYER_LEVEL = 120;

/** XP required to go FROM level n TO level n+1. */
export function xpForLevel(level: number): number {
  // Long-tail growth: fast first unlocks, then a steady climb for many sessions.
  return Math.round(85 + (level - 1) * 28 + Math.pow(level - 1, 2) * 2.4);
}

export interface SaveData {
  version: number;
  clientRev: number;         // monotonic; bumped on every flush. For cloud sync conflict resolution.
  tutorialCompleted: boolean;
  playerLevel: number;
  playerXp: number;          // XP toward next level (resets on level up)
  totalXp: number;           // lifetime XP earned (for stats)
  skillPoints: number;       // unspent
  spentSkillPoints: Record<string, number>; // node id -> rank
  campaign: {
    // map key -> highest difficulty cleared (0=none, 1=Squire, 2=Knight, 3=Lord)
    cleared: Record<string, number>;
  };
  codex: {
    enemiesKilled: Record<string, number>;
    bossesKilled: Record<string, number>;
    towersBuilt: Record<string, number>;
  };
  stats: {
    runsPlayed: number;
    runsWon: number;
    wavesCleared: number;
  };
  achievements: {
    unlocked: Record<string, number>;   // id -> unix ms timestamp
    progress: Record<string, number>;   // id -> current count for incremental defs
  };
  quests: {
    completed: Record<string, number>; // id -> unix ms timestamp
    // Snapshot of a target quest's raw stat value, taken while its level is
    // still locked. Progress is (raw - baseline), so stats accrued before the
    // quest unlocks don't count toward it.
    baselines: Record<string, number>;
  };
  // Legacy; banner feature removed, fields kept for save compatibility.
  unlockedBanners: string[];
  equippedBanner: string | null;
  avatarId: string | null;   // local mirror of profiles.avatar_id for instant render
  preferences: {
    /** When false, the player explicitly opted out of the tower-sale dialog. */
    confirmTowerSales: boolean;
  };
  audio: {
    master: number;     // 0..1
    music: number;      // 0..1
    sfx: number;        // 0..1
    muted: boolean;
    lateNight: boolean; // halves effective master volume
  };
}

function freshSave(): SaveData {
  return {
    version: SAVE_VERSION,
    clientRev: 0,
    tutorialCompleted: false,
    playerLevel: 1,
    playerXp: 0,
    totalXp: 0,
    skillPoints: 0,
    spentSkillPoints: {},
    campaign: { cleared: {} },
    codex: { enemiesKilled: {}, bossesKilled: {}, towersBuilt: {} },
    stats: { runsPlayed: 0, runsWon: 0, wavesCleared: 0 },
    achievements: { unlocked: {}, progress: {} },
    quests: { completed: {}, baselines: {} },
    unlockedBanners: [],
    equippedBanner: null,
    avatarId: null,
    preferences: { confirmTowerSales: true },
    audio: {
      master: AUDIO_DEFAULTS.master,
      music:  AUDIO_DEFAULTS.music,
      sfx:    AUDIO_DEFAULTS.sfx,
      muted:  AUDIO_DEFAULTS.muted,
      lateNight: AUDIO_DEFAULTS.lateNight
    }
  };
}

/**
 * Pluggable storage backend. Synchronous to keep the SaveSystem API simple;
 * the cloud implementation will buffer writes in memory and flush async in
 * the background, so this contract still holds.
 */
export interface SaveStore {
  read(): SaveData | null;
  write(data: SaveData): void;
}

/**
 * Backfill any nested objects that may be missing from an older save (additive
 * only — no migration). Merges sub-keys so a save missing e.g. `preferences`
 * never produces `data.preferences === undefined`. Safe to call multiple times
 * (idempotent). Applied in SaveSystem so it covers every SaveStore backend.
 */
function backfillSave(parsed: SaveData): SaveData {
  const fresh = freshSave();
  const merged: SaveData = { ...fresh, ...parsed, version: SAVE_VERSION } as SaveData;
  merged.audio        = { ...fresh.audio,        ...(parsed.audio        ?? {}) };
  merged.codex        = { ...fresh.codex,        ...(parsed.codex        ?? {}) };
  merged.stats        = { ...fresh.stats,        ...(parsed.stats        ?? {}) };
  merged.campaign     = { ...fresh.campaign,     ...(parsed.campaign     ?? {}) };
  merged.achievements = { ...fresh.achievements, ...(parsed.achievements ?? {}) };
  merged.quests       = { ...fresh.quests,       ...(parsed.quests       ?? {}) };
  merged.preferences  = { ...fresh.preferences,  ...(parsed.preferences  ?? {}) };
  return merged;
}

class LocalSaveStore implements SaveStore {
  read(): SaveData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      if (!parsed || parsed.version !== SAVE_VERSION) return null;
      return backfillSave(parsed as SaveData);
    } catch (err) {
      console.warn("[save] local read failed", err);
      return null;
    }
  }
  write(data: SaveData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn("[save] local write failed", err);
    }
  }
}

let activeStore: SaveStore = new LocalSaveStore();

/**
 * Swap the storage backend. Must be called BEFORE `save.load()`. Used by the
 * cloud sync layer to install a CloudSaveStore once the user is signed in.
 */
export function setSaveStore(store: SaveStore): void {
  activeStore = store;
}

/**
 * Reset the storage backend to browser localStorage. Called by the cloud sync
 * layer on sign-out so subsequent play/writes go to the local mirror again.
 */
export function useLocalSaveStore(): void {
  activeStore = new LocalSaveStore();
}

/**
 * Outbound event payload for the achievement layer. Save fires these from
 * its existing record* methods so the achievement system can stay decoupled
 * (no import cycle: Save knows nothing about Achievements; Achievements
 * registers a callback via `setSaveEventNotifier`).
 */
export type SaveEvent =
  | { kind: "wave";    waveNumber: number; difficultyMult: number }
  | { kind: "runWon";  mapKey: string; difficulty: number }
  | { kind: "kill";    enemyKey: string; isBoss: boolean }
  | { kind: "built";   towerKey: string }
  | { kind: "level";   playerLevel: number }
  | { kind: "skillSpent"; total: number };

let saveEventNotifier: ((e: SaveEvent) => void) | null = null;
const saveEventListeners: ((e: SaveEvent) => void)[] = [];
export function setSaveEventNotifier(fn: (e: SaveEvent) => void): void {
  saveEventNotifier = fn;
}
/** Subscribe without replacing the achievement bridge. Returns an unsubscribe. */
export function onSaveEvent(fn: (e: SaveEvent) => void): () => void {
  saveEventListeners.push(fn);
  return () => {
    const index = saveEventListeners.indexOf(fn);
    if (index >= 0) saveEventListeners.splice(index, 1);
  };
}
function emit(e: SaveEvent): void {
  saveEventNotifier?.(e);
  for (const listener of [...saveEventListeners]) listener(e);
}

class SaveSystem {
  private data: SaveData = freshSave();
  /** Real stored campaign state before the dev-server testing override. */
  private persistedCampaignCleared: Record<string, number> = {};
  private listeners: ((d: SaveData) => void)[] = [];
  private loaded = false;

  private applyDevCampaignOverride(): void {
    if (!import.meta.env.DEV) return;
    for (const mapKey of DEV_CAMPAIGN_MAP_KEYS) {
      this.data.campaign.cleared[mapKey] = 3;
    }
  }

  /**
   * The save as progression must judge it. The dev campaign override is a
   * testing view of `campaign.cleared`, so anything that hands out permanent
   * rewards — achievements and the XP and skill points they carry — has to read
   * the real stored progress instead, or a single dev-server run silently gifts
   * the whole campaign's rewards.
   */
  realProgress(): SaveData {
    if (!import.meta.env.DEV) return this.data;
    return { ...this.data, campaign: { cleared: { ...this.persistedCampaignCleared } } };
  }

  load(): SaveData {
    if (this.loaded) return this.data;
    this.loaded = true;
    const stored = activeStore.read();
    if (stored) this.data = backfillSave(stored);
    this.persistedCampaignCleared = { ...this.data.campaign.cleared };
    this.applyDevCampaignOverride();
    return this.data;
  }

  /**
   * Force a re-read from the (possibly newly-swapped) active store, bypassing
   * the load-once cache. Used by the cloud sync layer on sign-in/sign-out so
   * in-memory progress reflects the correct backend. Notifies listeners so the
   * UI refreshes immediately.
   */
  reload(): SaveData {
    this.loaded = true;
    const stored = activeStore.read();
    this.data = stored ? backfillSave(stored) : freshSave();
    this.persistedCampaignCleared = { ...this.data.campaign.cleared };
    this.applyDevCampaignOverride();
    for (const fn of this.listeners) fn(this.data);
    return this.data;
  }

  get(): SaveData { return this.data; }

  /** Persist via the active store. Cheap; safe to call on every change. */
  private flush(): void {
    this.data.clientRev++;
    // Dev-only map completion is a testing view, not real player progress.
    // Strip it from writes so running the dev server against a signed-in
    // account cannot permanently unlock the campaign in local/cloud saves.
    const persistedData = import.meta.env.DEV
      ? {
          ...this.data,
          campaign: { cleared: { ...this.persistedCampaignCleared } },
        }
      : this.data;
    activeStore.write(persistedData);
    for (const fn of this.listeners) fn(this.data);
  }

  /** Public flush. Used by external systems (e.g. Achievements) that mutate
   *  SaveData directly and need to bump clientRev / persist. */
  markDirty(): void { this.flush(); }

  on(fn: (d: SaveData) => void): void { this.listeners.push(fn); }

  /** Add XP. Returns the number of levels gained (0 if none). */
  addXp(amount: number): number {
    if (amount <= 0) return 0;
    this.data.playerXp += amount;
    this.data.totalXp += amount;
    let levels = 0;
    while (this.data.playerLevel < MAX_PLAYER_LEVEL) {
      const need = xpForLevel(this.data.playerLevel);
      if (this.data.playerXp < need) break;
      this.data.playerXp -= need;
      this.data.playerLevel++;
      this.data.skillPoints++;
      levels++;
    }
    if (this.data.playerLevel >= MAX_PLAYER_LEVEL) {
      this.data.playerXp = 0; // stop accruing toward a non-existent next level
    }
    this.flush();
    if (levels > 0) emit({ kind: "level", playerLevel: this.data.playerLevel });
    return levels;
  }

  recordWaveCleared(waveNumber: number, difficultyMult: number = 1): number {
    const endlessBonus = waveNumber > 60 ? 1 + Math.min(1.25, (waveNumber - 60) / 80) : 1;
    const xp = Math.round((24 + waveNumber * 8) * difficultyMult * endlessBonus);
    this.data.stats.wavesCleared++;
    const gained = this.addXp(xp);
    emit({ kind: "wave", waveNumber, difficultyMult });
    return gained;
  }

  recordRunStart(): void {
    this.data.stats.runsPlayed++;
    this.flush();
  }

  recordRunWon(mapKey: string, difficulty: number): void {
    this.data.stats.runsWon++;
    const prev = this.data.campaign.cleared[mapKey] ?? 0;
    if (difficulty > prev) this.data.campaign.cleared[mapKey] = difficulty;
    this.flush();
    emit({ kind: "runWon", mapKey, difficulty });
  }

  recordEnemyKilled(key: string, isBoss: boolean): void {
    const bucket = isBoss ? this.data.codex.bossesKilled : this.data.codex.enemiesKilled;
    bucket[key] = (bucket[key] ?? 0) + 1;
    // Codex updates are noisy; flush is still cheap but we batch via frame end:
    this.dirtyFlushScheduled || this.scheduleFlush();
    emit({ kind: "kill", enemyKey: key, isBoss });
  }

  recordTowerBuilt(key: string): void {
    this.data.codex.towersBuilt[key] = (this.data.codex.towersBuilt[key] ?? 0) + 1;
    this.dirtyFlushScheduled || this.scheduleFlush();
    emit({ kind: "built", towerKey: key });
  }

  // --- Skill point spending (P3 will use this) ---
  spendSkillPoint(nodeId: string, maxRank: number = 1): boolean {
    const current = this.data.spentSkillPoints[nodeId] ?? 0;
    if (current >= maxRank) return false;
    if (this.data.skillPoints <= 0) return false;
    this.data.skillPoints--;
    this.data.spentSkillPoints[nodeId] = current + 1;
    this.flush();
    let total = 0;
    for (const k of Object.keys(this.data.spentSkillPoints)) total += this.data.spentSkillPoints[k];
    emit({ kind: "skillSpent", total });
    return true;
  }

  refundAllSkillPoints(): void {
    let refunded = 0;
    for (const k of Object.keys(this.data.spentSkillPoints)) {
      refunded += this.data.spentSkillPoints[k];
    }
    this.data.spentSkillPoints = {};
    this.data.skillPoints += refunded;
    this.flush();
  }

  resetAll(): void {
    this.data = freshSave();
    this.persistedCampaignCleared = {};
    this.applyDevCampaignOverride();
    this.flush();
  }

  // --- Internal: batch flush for hot paths (codex updates per kill) ---
  private dirtyFlushScheduled = false;
  private scheduleFlush(): void {
    this.dirtyFlushScheduled = true;
    queueMicrotask(() => {
      this.dirtyFlushScheduled = false;
      this.flush();
    });
  }
}

export const save = new SaveSystem();
